/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
// Redux Toolkit
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/userSlice";
// BASE components
import BASEPageSimple from "../../../@BASE/core/BASEPageSimple";
import BASESvgIcon from "../../../@BASE/core/BASESvgIcon";
import useThemeMediaQuery from "../../../@BASE/hooks/useThemeMediaQuery";
import NavLinkAdapter from "../../../@BASE/core/NavLinkAdapter";
import authRoles from "../../auth/authRoles";
// Material-UI components
import { motion } from 'framer-motion';
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography";
// Components
import ListingSidebarContent from "../../shared-components/layouts/ListingSidebarContent";
import ProjectsList from "./listing/ProjectsList";
import DashboardHeader from "../../shared-components/headers/DashboardHeader";
// actions
import { resetNavigation } from "../../store/BASE/navigationSlice";
// BASE Page Simple styles
const Root = styled(BASEPageSimple)(({ theme }) => ({
  "& .BASEPageSimple-header": {
    // backgroundImage: `url("${window.location.origin.toString()}/assets/images/backgrounds/dashboard.jpg")`,
    background: 'linear-gradient(45deg, rgba(25,22,84,1) 0%, rgba(1,59,177,1) 40%, rgba(128,242,231,1) 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: theme.palette.getContrastText(theme.palette.primary.main),
  },
}));

function ProjectsPage(props) {
  // sidebar open state
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  // define page layout
  const pageLayout = useRef(null);
  // set mobile breakpoint
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("lg"));
  // dispatch
  const dispatch = useDispatch();
  // route params
  const routeParams = useParams();
  // get user data
  const user = useSelector(selectUser);
  // get data from API 
  useEffect(() => {
    // reset navigation
    dispatch(resetNavigation());
  }, [dispatch]);
  // If the route changes, open/close the sidebar.
  useEffect(() => {
    setRightSidebarOpen(Boolean(routeParams.id));
  }, [routeParams]);

  // render out the component
  return (
    <Root
      header={
        <DashboardHeader />
      }
      content={
        <div className="container">
          <div className="p-24 sm:p-32 w-full flex  space-y-8 sm:space-y-0 items-center justify-between">
            <div className="flex flex-col space-y-8 sm:space-y-0">
              <motion.span
                className="flex items-end"
                initial={{ x: -20 }}
                animate={{ x: 0, transition: { delay: 0.2 } }}
              >
                <Typography
                  className="text-2xl md:text-3xl font-extrabold tracking-tight leading-none grow"
                >
                  Projects
                </Typography>
              </motion.span>
            </div>
            <div className="flex items-center">
              {(authRoles.admin.includes(user.role)) && (
                <Button
                  variant="contained"
                  color="secondary"
                  className="whitespace-nowrap"
                  component={NavLinkAdapter}
                  to="/projects/new/edit"
                >
                  <BASESvgIcon size={20}>heroicons-outline:plus</BASESvgIcon>
                  <span className="hidden sm:flex mx-8">Add</span>
                </Button>
              )}
            </div>
          </div>
          <ProjectsList />
        </div>
      }
      ref={pageLayout}
      rightSidebarContent={<ListingSidebarContent resource='projects' />}
      rightSidebarOpen={rightSidebarOpen}
      rightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={640}
      rightSidebarVariant="temporary"
      scroll={isMobile ? "normal" : "normal"}
    />
  );
}

export default ProjectsPage;
